import React, { useMemo } from 'react'
import { IconButton, Stack, Typography } from '@mui/material'
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import type { Auction } from '@otw/models'
import { getUserDisplayName, useUsers } from 'hooks/useUsers'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import type { StatusTone } from 'core/theme.tokens'

export interface AuctionCardListProps {
  auctions: Auction[]
  onEdit: (auction: Auction) => void
  onDelete: (id: string) => void
}

const formatCreatedAt = (value: Auction['created_at']): string => {
  if (!value) return '—'
  let date: Date | null = null
  if (value instanceof Date) {
    date = value
  } else if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybe = (value as { toDate?: () => Date }).toDate?.()
    if (maybe instanceof Date) date = maybe
  } else if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) date = parsed
  }
  if (!date) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

const gatePassTone = (needed: boolean): StatusTone => (needed ? 'warning' : 'neutral')

const AuctionCardList: React.FC<AuctionCardListProps> = ({ auctions, onEdit, onDelete }) => {
  const { users } = useUsers()

  const userNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const user of users) {
      map.set(String(user.id), getUserDisplayName(user))
    }
    return map
  }, [users])

  if (auctions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No auctions yet. Add auction houses here and set whether gate passes are required.
      </Typography>
    )
  }

  return (
    <MobileRecordList>
      {auctions.map((auction) => {
        const createdBy = auction.created_by
          ? userNameById.get(String(auction.created_by)) || 'Unknown'
          : '—'
        return (
          <MobileRecordCard
            key={auction.id}
            title={auction.name}
            meta={`Created ${formatCreatedAt(auction.created_at)} · ${createdBy}`}
            status={{
              tone: gatePassTone(Boolean(auction.gate_passes_needed)),
              label: auction.gate_passes_needed ? 'Gate passes' : 'No gate pass',
            }}
            trailing={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    onEdit(auction)
                  }}
                  aria-label="Edit auction"
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation()
                    if (auction.id) onDelete(auction.id)
                  }}
                  aria-label="Delete auction"
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
            onClick={() => onEdit(auction)}
          />
        )
      })}
    </MobileRecordList>
  )
}

export default AuctionCardList
